import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import styles from "./NotificationEmailEditModal.module.scss";
import {
  createEmailTemplateItem,
  getEmailTemplateItem,
  getEmailTemplateTranslation,
  setEmailTemplateTranslation,
  handleClearDraftItem,
  handleUpdateFieldDraftItem,
  updateEmailTemplateItem,
  UpdateEmailTemplateItemPayload,
  clearEmailTranslation,
} from "store/emails/slice";
import { ModalDefTabs } from "configs/types";
import { EmailMainTab } from "../tabs/email/EmailMainTab";
import EmailLocalesTab from "../tabs/email/EmailLocalTab";
import { Modal } from "components/Modal/Modal";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { handleApiError } from "helpers/handleApiError";

const NotificationEmailEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector(
    (store) => store["emails"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["emails"].isItemFetching,
  );
  const payload = useAppSelector((store) => store["emails"].draftItem);
  const payloadTranslation = useAppSelector(
    (store) => store["emails"].translations,
  );
  const types = useAppSelector((store) => store["emails"].types);
  const entityId = searchParams.get("entityId") ?? "";

  const imageHeader = useRef<{ getImage: () => File | null } | null>(null);

  const [tabs] = useState<ModalDefTabs[]>([
    ModalDefTabs.main,
    ModalDefTabs.local,
  ]);

  const [activeTab, setActiveTab] = useState<ModalDefTabs>(ModalDefTabs.main);
  const localesList = useAppSelector((store) => store.settings.locales);
  const [locale, setLocale] = useState<string>(localesList[0]);
  const permissions = usePermissionHelper();
  const canEdit = entityId ? permissions?.update : permissions?.create;

  const isLoaded = () => {
    if (entityId !== "" && !isItemFetched) {
      return false;
    }

    return true;
  };

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const handleSave = async () => {
    if (!canEdit) return;

    if (!payload) return null;

    const { headerImageRef, ...rest } = payload;

    if (activeTab === ModalDefTabs.main && payload) {
      if (!imageHeader.current?.getImage() && !headerImageRef) {
        dispatch(
          showNotification({
            title: t("error"),
            message:
              searchParams.get("entityId") !== null
                ? t("entityUpdateError")
                : t("entityCreateError"),
            type: NotificationTypes.error,
          }),
        );

        return;
      }

      dispatch(
        entityId
          ? updateEmailTemplateItem({
              ...rest,
              ...(imageHeader.current?.getImage()
                ? { header: imageHeader.current?.getImage() as File }
                : {}),
            })
          : createEmailTemplateItem({
              ...rest,
              isActive: rest.isActive ? 1 : 0,
              header: imageHeader.current?.getImage() as File,
            }),
      )
        .unwrap()
        .then(() => {
          dispatch(
            showNotification({
              title: t("success"),
              message:
                searchParams.get("entityId") !== null
                  ? t("entityUpdateSuccess")
                  : t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );
          if (!entityId) {
            handleClose();
          }
        })
        .catch((error) => {
          handleApiError({ error, t, dispatch });
        });
    }

    if (activeTab === ModalDefTabs.local && entityId) {
      dispatch(
        setEmailTemplateTranslation({
          id: entityId,
          payload: {
            locale: locale,
            title: payloadTranslation[locale].title,
            isActive: payloadTranslation[locale].isActive ? 1 : 0,
            content: payloadTranslation[locale].content,
          },
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(
            showNotification({
              title: t("success"),
              message:
                searchParams.get("entityId") !== null
                  ? t("entityUpdateSuccess")
                  : t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );

          if (!entityId) {
            handleClose();
          }
        })
        .catch((error) => {
          handleApiError({ error, t, dispatch });
        });
    }
  };

  const handleUpdateField = <K extends keyof UpdateEmailTemplateItemPayload>(
    key: K,
    value: UpdateEmailTemplateItemPayload[K],
  ) => {
    const updatedField = { [key]: value };

    dispatch(handleUpdateFieldDraftItem(updatedField));
  };

  const handleTabClick = (tab: ModalDefTabs) => {
    setActiveTab(tab);
  };

  const handleChangeLocalTab = async (newLocale: string) => {
    if (newLocale === locale || !entityId) return;
    dispatch(clearEmailTranslation(newLocale));
    setLocale(newLocale);
  };

  const renderTab = () => {
    if (activeTab === ModalDefTabs.main) {
      return (
        <EmailMainTab
          payload={payload}
          handleUpdateField={handleUpdateField}
          imageHeader={imageHeader}
          types={types}
          readOnly={!canEdit}
        />
      );
    }

    if (activeTab === ModalDefTabs.local) {
      return (
        <EmailLocalesTab
          activeLocale={locale}
          setActiveLocale={handleChangeLocalTab}
          readOnly={!canEdit}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    if (entityId !== "" && !isItemFetched) {
      dispatch(getEmailTemplateItem(entityId));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    if (entityId) {
      dispatch(
        getEmailTemplateTranslation({
          id: entityId,
          locale,
        }),
      );
    }
  }, [locale]);

  return (
    <Modal onClose={handleClose}>
      <Modal.Header>{t("emainNotification")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={isItemFetched}
            onClick={(t) => {
              if (isItemFetched) {
                handleTabClick(t);
              }
            }}
          />

          <Modal.Content
            className={styles.notificationEmailEditModalContentWrapper}
          >
            <div className={styles["email-content"]}>{renderTab()}</div>
          </Modal.Content>

          {canEdit && (
            <Modal.Footer>
              <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
              <Button
                isPrimary
                handleClick={handleSave}
                txt={t("save")}
                isLoad={isItemFetching}
              />
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default NotificationEmailEditModal;
